/**
 * 邮件签名选择器组件 — 工具栏中的下拉式签名切换按钮。
 *
 * 主要导出：`CrmSignatureSelector`（自定义元素 `<crm-signature-selector>`）
 *
 * 功能特性：
 * - 下拉菜单展示当前账户的所有签名列表
 * - 高亮显示当前激活的签名（蓝点指示 + 对勾标记）
 * - 支持切换激活签名、移除签名
 * - 点击组件外部自动关闭下拉菜单
 * - 每次打开下拉时刷新签名列表
 *
 * 依赖关系：
 * - 继承 `BaseElement`
 * - 通过 `emailStore` 获取签名列表和切换激活签名
 *
 * 对外事件：
 * - `crm-signature-changed` — 切换签名后冒泡，携带选中的签名对象
 * - `crm-signature-removed` — 点击移除签名后冒泡
 */
import { css, html } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';
import { BaseElement } from '../base-element.js';
import { emailStore } from '../store.js';
import type { EmailSignature } from '../types.js';

@customElement('crm-signature-selector')
export class CrmSignatureSelector extends BaseElement {
  /** 当前邮件账户 ID */
  @property({ type: Number }) accountId = 0;

  /** 下拉菜单是否展开 */
  @state() private open = false;
  /** 签名列表 */
  @state() private signatures: EmailSignature[] = [];
  /** 当前激活的签名对象 */
  @state() private activeSig: EmailSignature | null = null;
  /** 签名请求序号，仅允许最后一次账户切换请求更新选择器。 */
  private _loadRequestId = 0;

  static styles = css`
    :host { display: inline-flex; align-items: center; position: relative; }
  `;

  /** 生命周期：连接 DOM 时加载签名列表并注册外部点击监听 */
  connectedCallback() {
    super.connectedCallback();
    this.loadSignatures();
    document.addEventListener('click', this.handleOutsideClick);
  }

  /** 生命周期：从 DOM 移除时移除外部点击监听 */
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.handleOutsideClick);
  }

  /**
   * 外部点击处理 — 当下拉菜单打开且点击发生在组件外部时关闭菜单。
   * @param e - 鼠标点击事件
   */
  private handleOutsideClick = (e: MouseEvent) => {
    if (this.open && !this.contains(e.target as Node)) {
      this.open = false;
    }
  };

  /**
   * 加载签名列表 — 从 emailStore 获取指定账户的所有签名。
   * 同时更新当前激活签名的引用。
   */
  private async loadSignatures() {
    const requestId = ++this._loadRequestId;
    const accountId = this.accountId;
    if (!accountId) return;
    await emailStore.fetchSignatures(accountId);
    if (requestId !== this._loadRequestId || accountId !== this.accountId) return;
    this.signatures = emailStore.state.signatures[accountId] || [];
    this.activeSig = this.signatures.find(s => s.is_active) || null;
  }

  /**
   * 生命周期：属性变化后触发。
   * 当 accountId 变化时重新加载签名列表。
   * @param changedProps - 发生变化的属性 Map
   */
  updated(changedProps: Map<string, unknown>) {
    if (changedProps.has('accountId') && this.accountId) {
      this.loadSignatures();
    }
  }

  /**
   * 选择签名 — 调用 API 激活该签名，更新本地状态并派发事件。
   * @param sig - 选中的签名对象
   * @sideeffect 调用后端激活接口，更新 activeSig/signatures/open，派发 `crm-signature-changed` 事件
   */
  private async selectSignature(sig: EmailSignature) {
    await emailStore.activateSignature(this.accountId, sig.id);
    this.activeSig = sig;
    this.signatures = emailStore.state.signatures[this.accountId] || [];
    this.open = false;
    this.dispatchEvent(new CustomEvent('crm-signature-changed', {
      detail: sig, bubbles: true, composed: true,
    }));
  }

  /**
   * 移除签名 — 派发 `crm-signature-removed` 事件，由父组件处理实际移除逻辑。
   * @param e - 点击事件（用于阻止冒泡，避免触发 toggleOpen）
   */
  private removeSignature(e: Event) {
    e.stopPropagation();
    this.open = false;
    this.dispatchEvent(new CustomEvent('crm-signature-removed', {
      bubbles: true, composed: true,
    }));
  }

  /**
   * 切换下拉菜单展开/收起。
   * 展开时重新加载签名列表以获取最新数据。
   * @param e - 点击事件（用于阻止冒泡，避免触发 handleOutsideClick）
   */
  private toggleOpen(e: Event) {
    e.stopPropagation();
    this.open = !this.open;
    if (this.open) this.loadSignatures();
  }

  /**
   * 渲染签名选择器。
   * 布局：工具栏按钮（图标 + "签名"文字 + 激活指示圆点）→ 下拉菜单（移除选项 + 签名列表）。
   * @returns Lit 模板
   */
  render() {
    const hasActive = this.activeSig !== null;
    return html`
      <button @click=${this.toggleOpen}
        title=${hasActive ? this.activeSig!.description : '未配置签名'}
        class="toolbar-btn"
        style="display:flex;align-items:center;gap:3px;">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M20 14.66V20a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2h5.34"/>
          <path d="M18 2l4 4-10 10H8v-4L18 2z"/>
        </svg>
        <span style="font-size:12px;">签名</span>
        ${hasActive ? html`
          <span style="position:absolute;top:2px;right:2px;width:6px;height:6px;background:#0d74ce;border-radius:50%;display:inline-block;"></span>
        ` : ''}
      </button>

      ${this.open ? html`
        <div @click=${(e: Event) => e.stopPropagation()}
          style="position:absolute;top:100%;right:0;z-index:50;margin-top:4px;border:1px solid #e0e1e6;border-radius:8px;background:#fff;box-shadow:rgba(0,0,0,0.1) 0px 10px 20px;width:260px;max-height:280px;overflow-y:auto;">
          <div @click=${this.removeSignature}
            style="padding:8px 12px;border-radius:6px;cursor:pointer;color:#60646c;font-size:13px;border-bottom:1px solid #e0e1e6;">
            移除签名
          </div>
          ${this.signatures.length === 0 ? html`
            <div style="padding:12px;font-size:13px;color:#b0b4ba;text-align:center;">暂无签名</div>
          ` : this.signatures.map(s => html`
            <div @click=${() => this.selectSignature(s)}
              style="padding:8px 12px;border-radius:6px;cursor:pointer;display:flex;justify-content:space-between;align-items:center;${s.is_active ? 'background:#f0f0f3;' : ''}">
              <div>
                <div style="font-size:13px;font-weight:${s.is_active ? 600 : 400};color:#1c2024;">${s.description}</div>
              </div>
              ${s.is_active ? html`<span style="color:#0d74ce;font-size:16px;">✓</span>` : ''}
            </div>
          `)}
        </div>
      ` : ''}
    `;
  }
}
